// Organizer Results Status (card 96222320): labels, search, grouping and
// running order for the page. Pure functions over GET
// /feis/result-status/:feisId; the admin app carries a verbatim copy.
import { sortCompetitions } from '../../../../helper/SortSyllabus';

// One list in the order the tabulator works it; Published last (the page
// keeps it collapsed). No view tabs (Devin 2026-10-04).
export const SECTIONS = [
  { state: 'ready', title: 'Ready to publish' },
  { state: 'progress', title: 'Scores in progress' },
  { state: 'none', title: 'No scores yet' },
  { state: 'published', title: 'Published' },
];

const nice = (s) => {
  const t = String(s || '').trim();
  return t ? t.charAt(0).toUpperCase() + t.slice(1).toLowerCase() : '';
};

// Competition label: level + dance + one age label, e.g. "Novice Reel ·
// Under 15", "Prelim Championship · 13 and over" (Devin 2026-10-04: no list
// of age groups). Specials, teams, non-level, arts and culture and other
// named competitions keep their own name; adult levels say "Adult" unless
// the name already does; a name that already carries an age gets no age
// label; "second chance" is spelled one way.
const LEVELS = {
  'FIRST FEIS': 'First Feis',
  BEGINNER: 'Beginner',
  'BEGINNER 1': 'Beginner 1',
  'ADVANCED BEGINNER': 'Advanced Beginner',
  'BEGINNER 2': 'Beginner 2',
  NOVICE: 'Novice',
  PRIZEWINNER: 'Prizewinner',
  'PRELIMINARY CHAMPIONSHIPS': 'Prelim',
  'OPEN CHAMPIONSHIPS': 'Open',
};
const levelOf = (group) => {
  const g = String(group || '').trim().toUpperCase();
  if (LEVELS[g]) return LEVELS[g];
  if (/^ADULT/.test(g)) return 'Adult';
  return ''; // SPECIALS, TEAMS, NON-LEVEL, ARTS AND CULTURE, PARENT & CHILD, event-specific groups
};
const titleCase = (s) =>
  String(s || '')
    .trim()
    .replace(/\s+/g, ' ')
    .toLowerCase()
    .replace(/(^|[\s/(&-])([a-z])/g, (m, a, b) => a + b.toUpperCase());
const SECOND = /(\s*-\s*second(\s+chance)?|\s+second\s+chance)$|^second\s+chance\s*-\s*/i;

const danceOf = (c) => {
  const raw = String(c?.description || c?.name || '').trim();
  const second = SECOND.test(raw);
  const d = raw.replace(SECOND, '').trim();
  const dance = /^(prelim|open champ)$/i.test(d) ? 'Championship' : titleCase(d);
  return second ? `${dance} (second chance)` : dance;
};
const hasAge = (name) => /\bU\d{1,2}\b|\bunder\s*\d{1,2}\b|\d{1,2}\s*(&|and)\s*over\b/i.test(String(name || ''));

// The age groups as one label: "Under <oldest>", or "<youngest> and over"
// when the range reaches an "and over" group (Under 16 and up = 15 and
// over); from the youngest group (Under 5 or younger) to an "and over"
// group is "All ages".
export const ageLabel = (ages) => {
  const list = (ages || []).map((a) => String(a).trim()).filter(Boolean);
  const unders = list.map((a) => /^U(\d+)$/i.exec(a)).filter(Boolean).map((m) => Number(m[1]));
  const over = list.map((a) => /^(\d+)\s*(AND|&)\s*OVER$/i.exec(a)).filter(Boolean).map((m) => Number(m[1]));
  if (over.length) {
    if (!unders.length) return `${Math.min(...over)} and over`;
    const youngest = Math.min(...unders);
    return youngest <= 5 ? 'All ages' : `${youngest - 1} and over`;
  }
  if (unders.length) return `Under ${Math.max(...unders)}`;
  return list.length === 1 ? titleCase(list[0]) : '';
};

export const compTitle = (c) => {
  const level = levelOf(c?.group);
  const dance = danceOf(c);
  return level && !dance.toLowerCase().includes(level.toLowerCase()) ? `${level} ${dance}` : dance;
};
export const compLabel = (c) => [compTitle(c), hasAge(c?.description || c?.name) ? '' : ageLabel(c?.ages)].filter(Boolean).join(' · ');
// level name as entered, for search
export const compSub = (c) => [(c?.ages || []).filter(Boolean).join(', '), nice(c?.group)].filter(Boolean).join(' · ');

// "Stage 2 · 10:40 AM"
export const whenLabel = (c) => {
  const s = c?.schedule;
  if (!s) return '';
  const stage = String(s.stageName || '').trim() || `Stage ${s.stage}`;
  return [stage, String(s.startTime || '').trim()].filter(Boolean).join(' · ');
};

const fold = (s) =>
  String(s ?? '')
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase();

// Search: the code, or every typed word somewhere in the competition's name
// (description, level, ages). "212", "pc3", "under 9 reel" -> "u9 reel".
export const matches = (c, query) => {
  const q = fold(query).trim();
  if (!q) return true;
  if (fold(c?.code) === q || fold(c?.code).startsWith(q)) return true;
  // words match the name, level and ages only: a code matches from its start
  const hay = fold([c?.description, c?.group, (c?.ages || []).join(' '), compSub(c), compLabel(c)].join(' '));
  return q
    .replace(/\bunder\s*(\d+)/g, 'u$1')
    .split(/\s+/)
    .every((w) => hay.includes(w));
};

// Running order: scheduled competitions by stage then position on it, then
// everything unscheduled in the syllabus order (level, then code).
export const runningOrder = (comps) => {
  const list = comps || [];
  const scheduled = list
    .filter((c) => c.schedule)
    .sort(
      (a, b) =>
        a.schedule.stageIndex - b.schedule.stageIndex ||
        a.schedule.position - b.schedule.position ||
        String(a.code).localeCompare(String(b.code), undefined, { numeric: true })
    );
  const rest = sortCompetitions(list.filter((c) => !c.schedule));
  return [...scheduled, ...rest];
};

// Whether a competition can still take the action: the data may have changed
// since a confirm opened (the minute refresh, another tabulator).
export const canDo = (kind, c) => (kind === 'unpublish' ? c?.state === 'published' : c?.state === 'ready');

// A competition as it is once the server accepted the action - shown at once,
// so a slow or failed reload can never offer the same publish again.
export const afterAction = (kind, c, at) =>
  kind === 'unpublish'
    ? { ...c, published: false, publishedAt: null, state: c.complete ? 'ready' : c.scored > 0 ? 'progress' : 'none' }
    : { ...c, published: true, publishedAt: at, state: 'published' };

export const counts = (comps) => {
  const n = { unpublished: 0, published: 0, all: 0, ready: 0, progress: 0, none: 0 };
  (comps || []).forEach((c) => {
    n.all += 1;
    if (c.state === 'published') n.published += 1;
    else {
      n.unpublished += 1;
      n[c.state] = (n[c.state] || 0) + 1;
    }
  });
  return n;
};

// The sections for a search, each in running order; Published newest first.
// `pinned` (id -> { section, publishedAt, until }): a row published on this
// page stays in the section it was in, showing Published, for PIN_MS so it
// can be printed (Devin 2026-10-04: "Having it disappear on publish was
// jarring"; 90 seconds), then moves down to Published. An unpublished row
// goes straight back to its section.
export const PIN_MS = 90 * 1000;
// the pins still in force at `now`, and when the next one runs out
export const livePins = (pinned, now) =>
  Object.fromEntries(Object.entries(pinned || {}).filter(([, p]) => !(p.until <= now)));
export const nextExpiry = (pinned) => {
  const times = Object.values(pinned || {}).map((p) => p.until).filter(Number.isFinite);
  return times.length ? Math.min(...times) : null;
};

export const sectionsFor = (comps, query, pinned = {}) => {
  // a pin holds only while the competition is still published (another
  // tabulator may have unpublished it: then it goes to its real section)
  const sectionOf = (c) => (c.state === 'published' && pinned[String(c._id)]?.section) || c.state;
  const shown = runningOrder(comps).filter((c) => matches(c, query));
  const at = (c) => new Date(c.publishedAt || pinned[String(c._id)]?.publishedAt || 0);
  return SECTIONS.map((s) => {
    const rows = shown.filter((c) => sectionOf(c) === s.state);
    if (s.state === 'published') rows.sort((x, y) => at(y) - at(x));
    return { ...s, rows };
  }).filter((s) => s.rows.length);
};

const plural = (n, one, many = `${one}s`) => `${n} ${n === 1 ? one : many}`;
const cardList = (m) => {
  const listed = (m.cards || []).join(', ');
  const more = m.count - (m.cards || []).length;
  if (!listed) return plural(m.count, 'dancer');
  return `card${m.count === 1 ? '' : 's'} ${listed}${more > 0 ? ` and ${more} more` : ''}`;
};
const nameOf = (m) => String(m.judge || '').trim() || 'Unnamed judge';
const judgeOf = (m, c) => (c.rounds > 1 ? `${nameOf(m)}, round ${m.round}` : nameOf(m));
const listOf = (names) => (names.length > 3 ? `${names.slice(0, 3).join(', ')} and ${names.length - 3} more` : names.join(', '));

// Who is in. A competition does not record which judges sit on it (they are
// picked as each sheet is entered), so the page names the judges whose
// scores are in and never guesses the one still to come.
const inLine = (c) => {
  const entered = c.entered || [];
  if (!c.slotMode) return entered.length ? `In: ${listOf([...new Set(entered.map(nameOf))])}` : '';
  // 6/9-judge: three sheets a round; the rounds not yet full, with who is in
  const perRound = Math.max(1, Math.round(c.judgesExpected / Math.max(1, c.rounds)));
  const lines = [];
  for (let round = 1; round <= c.rounds; round++) {
    const names = entered.filter((e) => e.round === round).map(nameOf);
    if (names.length < perRound) lines.push(`Round ${round} in: ${names.length ? listOf(names) : 'none yet'}`);
  }
  return lines.join(' · ');
};

// What the status column says: a pill and one line of detail.
export const statusOf = (c) => {
  const unit = c.slotMode ? 'sheets' : 'judges';
  const shape = [
    c.slotMode ? '9 sheets' : plural(c.judgesExpected, 'judge'),
    c.rounds > 1 && !c.slotMode ? plural(c.rounds, 'round') : '',
    `${c.scored} danced`,
  ]
    .filter(Boolean)
    .join(' · ');
  // Gaps in sheets that were entered: a judge with a round not entered at
  // all (named: their other rounds are in), or single dancers left off (cards).
  const missing = c.missing || [];
  const whole = missing.filter((m) => m.count >= c.scored);
  const partial = missing.filter((m) => m.count < c.scored);
  const gapLine = partial.length
    ? `${judgeOf(partial[0], c)}: ${cardList(partial[0])}${partial.length > 1 ? ` (+${partial.length - 1} more)` : ''}`
    : whole.length
      ? `Waiting for ${listOf(whole.map((m) => judgeOf(m, c)))}`
      : '';
  if (c.state === 'none') return { tone: 'none', icon: 'schedule', label: 'No scores yet', detail: '' };
  if (c.state === 'ready') return { tone: 'ready', icon: 'task_alt', label: 'All scores in', detail: shape };
  const judgesShort = `${c.judgesIn} of ${c.judgesExpected} ${unit} in`;
  // score rows with no judge or no valid round (only Edit can fix them)
  const checkLine = c.malformed > 0 ? `${plural(c.malformed, 'score')} without a judge or round: open Edit to fix` : '';
  if (c.state === 'progress') {
    // publishing needs exactly the expected number: a wrong judge entered?
    if (c.judgesIn > c.judgesExpected)
      return { tone: 'progress', icon: 'error', label: `Too many ${unit}: ${c.judgesIn} of ${c.judgesExpected}`, detail: inLine(c) || gapLine };
    if (c.judgesIn < c.judgesExpected)
      return { tone: 'progress', icon: 'pending', label: judgesShort, detail: inLine(c) || gapLine };
    if (c.missingScores > 0)
      return { tone: 'progress', icon: 'error', label: `${plural(c.missingScores, 'score')} missing`, detail: gapLine };
    return { tone: 'progress', icon: 'error', label: `${plural(c.malformed || 0, 'score')} to check`, detail: checkLine };
  }
  // published (complete or not: the publish endpoint only checks judges)
  const at = c.publishedAt ? new Date(c.publishedAt) : null;
  const when = at && !Number.isNaN(at.getTime()) ? `Published ${at.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}` : 'Published';
  if (!c.complete)
    return { tone: 'published', icon: 'check_circle', label: when, detail: `Scores incomplete: ${c.judgesIn !== c.judgesExpected ? judgesShort : gapLine || checkLine}`, warn: true };
  return { tone: 'published', icon: 'check_circle', label: when, detail: shape };
};

// The schedule page (card a1f0fff5) reuses the naming rules for its rows.
export { levelOf, danceOf as danceName, hasAge, nice };
